import { collection, getDocs, orderBy, query } from 'firebase/firestore';

import { db } from '@/firebase-config';
import type { CueDepartment } from '@/features/prompt-book/types';
import { buildProjectSubcollectionPath, type ProjectScope } from '@/features/sync/project-scope';
import type { PerformanceLogClient } from '../contracts';
import type { PerformanceLog } from '@/features/performance/types';
import { isRecord, readArray, readNumber, readString } from './firestore-read-normalizers';

type PerformanceDocumentSnapshot = {
  id: string;
  data: () => unknown;
};

type PerformanceSnapshot = {
  docs: PerformanceDocumentSnapshot[];
};

const PERFORMANCES_COLLECTION = 'performances';
const CUE_DEPARTMENTS = new Set<CueDepartment>(['lx', 'sq', 'fs', 'deck', 'fly', 'av']);

function isCueDepartment(value: unknown): value is CueDepartment {
  return typeof value === 'string' && CUE_DEPARTMENTS.has(value as CueDepartment);
}

function mapRecordArray<T>(
  value: unknown,
  mapper: (record: Record<string, unknown>) => T | null,
): T[] {
  return readArray(value, isRecord)
    .flatMap((record) => {
      const mapped = mapper(record);
      return mapped ? [mapped] : [];
    });
}

function toFiredCue(value: Record<string, unknown>): PerformanceLog['firedCues'][number] | null {
  if (
    typeof value.cueId !== 'string' ||
    typeof value.firedAt !== 'number' ||
    typeof value.firedBy !== 'string'
  ) {
    return null;
  }

  return {
    cueId: value.cueId,
    firedAt: value.firedAt,
    firedBy: value.firedBy,
  };
}

function toShowMarker(value: Record<string, unknown>): PerformanceLog['markers'][number] | null {
  if (
    typeof value.id !== 'string' ||
    typeof value.label !== 'string' ||
    typeof value.timestamp !== 'number'
  ) {
    return null;
  }

  return {
    id: value.id,
    label: value.label,
    timestamp: value.timestamp,
  };
}

function toQuickNote(value: Record<string, unknown>): PerformanceLog['quickNotes'][number] | null {
  if (
    typeof value.id !== 'string' ||
    typeof value.text !== 'string' ||
    typeof value.timestamp !== 'number'
  ) {
    return null;
  }

  return {
    id: value.id,
    text: value.text,
    timestamp: value.timestamp,
  };
}

function toPerformanceCue(
  value: Record<string, unknown>,
): PerformanceLog['cueSequenceSnapshot'][number] | null {
  if (
    typeof value.id !== 'string' ||
    (value.type !== 'go' && value.type !== 'standby' && value.type !== 'warning') ||
    !isCueDepartment(value.department) ||
    typeof value.label !== 'string' ||
    typeof value.scriptContext !== 'string' ||
    typeof value.blockId !== 'string' ||
    typeof value.sourceAnchorId !== 'string'
  ) {
    return null;
  }

  return {
    id: value.id,
    type: value.type,
    department: value.department,
    label: value.label,
    scriptContext: value.scriptContext,
    blockId: value.blockId,
    sourceAnchorId: value.sourceAnchorId,
  };
}

function toPerformanceLog(docSnap: PerformanceDocumentSnapshot): PerformanceLog {
  const data = isRecord(docSnap.data()) ? docSnap.data() as Record<string, unknown> : {};
  const status = data.status === 'draft' || data.status === 'complete' ? data.status : 'complete';

  return {
    id: docSnap.id,
    status,
    date: readString(data.date),
    showStartTime: readNumber(data.showStartTime),
    showEndTime: readNumber(data.showEndTime),
    totalRunTime: readNumber(data.totalRunTime),
    firedCues: mapRecordArray(data.firedCues, toFiredCue),
    markers: mapRecordArray(data.markers, toShowMarker),
    quickNotes: mapRecordArray(data.quickNotes, toQuickNote),
    cueSequenceSnapshot: mapRecordArray(data.cueSequenceSnapshot, toPerformanceCue),
    createdBy: readString(data.createdBy),
    createdAt: readNumber(data.createdAt),
  };
}

export class FirebasePerformanceLogClient implements PerformanceLogClient {
  async listPerformanceLogs(scope: ProjectScope): Promise<PerformanceLog[]> {
    const colRef = collection(db, buildProjectSubcollectionPath(scope, PERFORMANCES_COLLECTION));
    const logsQuery = query(colRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(logsQuery) as PerformanceSnapshot;

    return snapshot.docs.map(toPerformanceLog);
  }
}
